import { ArticleNonParagraphProps } from "@pages/Article/components/ArticleParagraphElements/utils/types";
import { cn } from "@util/helpers";
import parse, { DOMNode, domToReact, Element } from "html-react-parser";

/**
 * Renders an inline image element in an article.
 * This component processes the body string to extract image and caption elements,
 * applying appropriate styles and classes.
 */
const InlineImage = ({
  bodyString,
  className,
  paragraphWidth,
}: ArticleNonParagraphProps): React.ReactElement => {
  const transformImage = (
    node: DOMNode
  ): string | boolean | void | object | Element | null => {
    if (node.type === "tag" && node.name === "div") {
      const el = node;
      const children = el.children as DOMNode[];

      return (
        <>
          {domToReact(children, {
            replace: (childNode: DOMNode) => {
              if (childNode.type !== "tag") return childNode;
              // Handle <img>
              if (childNode.name === "img") {
                const imgElement = childNode;
                const extraCss = imgElement.attribs["data-css-class"];
                const removedAttributes = ["class"];
                const attributes = Object.fromEntries(
                  Object.entries(imgElement.attribs).filter(
                    ([key]) => !removedAttributes.includes(key)
                  )
                );
                return (
                  <img
                    {...attributes}
                    src={imgElement.attribs.src}
                    alt={imgElement.attribs.alt || ""}
                    className={cn("block h-full w-full", extraCss)}
                  />
                );
              }

              // // Handle <figcaption>
              if (childNode.name === "figcaption") {
                const captionElement = childNode;
                const captionChildren = captionElement.children as DOMNode[];
                return (
                  <figcaption className="m-0 mt-1 w-full p-0 text-3xs text-gray-525">
                    {domToReact(captionChildren)}
                  </figcaption>
                );
              }

              return childNode;
            },
          })}
        </>
      );
    }
  };

  return (
    <div
      className={cn("mb-6 whitespace-normal", className, paragraphWidth)}
      data-testid="article-inline-image-component"
    >
      {parse(bodyString, { replace: transformImage })}
    </div>
  );
};

export default InlineImage;
